import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Auth, authState } from '@angular/fire/auth';
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  docData,
  Firestore,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from '@angular/fire/firestore';
import { catchError, forkJoin, from, map, Observable, of, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
export type ValidationStatus =
  | 'uploading'
  | 'pending'
  | 'queued'
  | 'processing'
  | 'validating'
  | 'completed'
  | 'validated_metadata'
  | 'failed'
  | 'failed_fields'
  | 'failed_storage'
  | 'failed_validation'
  | 'failed_loading';

export type DatasetFile = {
  id: string;
  name: string;
  file_type: string;
  file_url?: string;
  schema?: string;
  schema_id: string;
  type?: 'insert' | 'full_replace' | 'replace_scope';
  destination?: string;
  target?: { table?: string; schema?: string; project?: string };
  scope?: DatasetOperationScope | null;
  columns_snapshot?: string[];
  created_by?: string;
  created_by_name?: string;
  created_by_email?: string;
  created_by_uid?: string;
  created_at?: unknown;
  updated_at?: unknown;
  validation_status?: ValidationStatus;
  validation_message?: string;
  validation_code?: string;
  missing_required_fields?: string[];
  row_count?: number;
  deleted_row_count?: number;
  error_code?: string;
  last_error?: {
    at?: string;
    code?: string;
    stage?: string;
    message?: string;
    details?: unknown;
  };
};

export interface ParsedValidationError {
  missing: string[];
  detected: string[];
  extras: string[];
  reason: string;
  raw: string;
}

/**
 * El backend a veces devuelve el detalle de validación como texto plano en
 * `validation_message` (ej: "Faltan: A, B. Sobran: C. Headers detectados: x, y. Motivo: ...").
 * Este helper lo convierte en datos estructurados, conservando el texto crudo.
 * `labelFor` permite mapear nombres de columna a labels legibles cuando hay schema.
 */
export function parseCollectorDatasetValidationError(
  dataset: DatasetFile | null | undefined,
  labelFor?: (fieldName: string) => string
): ParsedValidationError | null {
  const raw = (dataset?.validation_message || dataset?.last_error?.message || '').trim();
  const structuredMissing = dataset?.missing_required_fields?.length
    ? [...dataset.missing_required_fields]
    : [];

  if (!raw && !structuredMissing.length) return null;

  const missing = structuredMissing.length
    ? structuredMissing.map((f) => (labelFor ? labelFor(f) : f))
    : extractValidationList(raw, 'Faltan:', ['Sobran:', 'Headers detectados:', 'Motivo:']);
  const extras = extractValidationList(raw, 'Sobran:', ['Headers detectados:', 'Motivo:']);
  const detected = extractValidationList(raw, 'Headers detectados:', ['Motivo:']);
  const reason = extractValidationSegment(raw, 'Motivo:', []);

  return { missing, detected, extras, reason, raw };
}

function extractValidationSegment(text: string, label: string, nextLabels: string[]): string {
  const start = text.indexOf(label);
  if (start === -1) return '';
  const rest = text.slice(start + label.length);
  let cut = rest.length;
  for (const next of nextLabels) {
    const idx = rest.indexOf(next);
    if (idx !== -1 && idx < cut) cut = idx;
  }
  return rest.slice(0, cut).replace(/\.\s*$/, '').trim();
}

function extractValidationList(text: string, label: string, nextLabels: string[]): string[] {
  const segment = extractValidationSegment(text, label, nextLabels);
  if (!segment) return [];
  return segment.split(',').map((item) => item.trim()).filter(Boolean);
}

export interface CollectorDatasetRowStats {
  loaded: number | null;
  deleted: number | null;
}

/**
 * Conteos de filas del resultado. El backend hoy los reporta dentro de
 * `validation_message` (ej: "se borraron 70462 filas ... se cargaron 70462 filas nuevas"),
 * así que los extraemos de ahí. Si en el futuro expone campos estructurados
 * (`row_count`, `deleted_row_count`), esos tienen prioridad.
 */
export function getCollectorDatasetRowStats(dataset: DatasetFile | null | undefined): CollectorDatasetRowStats {
  const message = dataset?.validation_message || '';
  const loaded = typeof dataset?.row_count === 'number'
    ? dataset.row_count
    : parseRowCountFromMessage(message, /se\s+cargaron\s+([\d.,]+)\s+filas/i);
  const deleted = typeof dataset?.deleted_row_count === 'number'
    ? dataset.deleted_row_count
    : parseRowCountFromMessage(message, /se\s+borraron\s+([\d.,]+)\s+filas/i);
  return { loaded, deleted };
}

function parseRowCountFromMessage(message: string, pattern: RegExp): number | null {
  const match = pattern.exec(message);
  if (!match) return null;
  const n = Number(match[1].replace(/[.,]/g, ''));
  return Number.isFinite(n) ? n : null;
}

export type DatasetOperationScope = {
  column: string;
  operator: 'eq' | 'in' | 'between' | 'gte' | 'lte';
  value: unknown;
};

export type CreateDatasetPayload = {
  name: string;
  file_type: string;
  schema: string;
  schema_id: string;
  file_url: string;
  type: 'insert' | 'full_replace' | 'replace_scope';
  columns_snapshot: string[];
  created_by: string;
  created_by_uid: string;
  validation_status: 'pending' | 'processing' | 'passed' | 'failed';
  validation_message?: string;
};

export type CreateCollectorDatasetResponse = {
  collector_dataset_id: string;
  schema_id: string;
  file_url: string;
  storage_path: string;
  validation_status: 'uploading' | 'queued' | 'processing' | 'validating' | 'completed' | 'failed';
  message: string;
};

export type DatasetPermission = {
  uid: string;
  name?: string;
  email?: string;
  role: 'owner' | 'editor' | 'reader';
};

@Injectable({ providedIn: 'root' })
export class CollectorDatasetsService {
  firestore = inject(Firestore);
  auth = inject(Auth);
  private readonly baseUrl = `${environment['api_backend']}/api`;
  private readonly collectorBucketName = 'slco_data_connector_dev';
  private readonly collectorFolderName = 'collector_datasets';

  constructor(private http: HttpClient) { }

  createCollectorDataset$(payload: {
    file: File;
    schema_id: string;
    schema_name: string;
    type: 'insert' | 'full_replace' | 'replace_scope';
    created_by_uid: string;
    created_by: string;
    sheet_name?: string;
    header_row?: number;
    confirm?: boolean;
    scope?: DatasetOperationScope;
  }): Observable<CreateCollectorDatasetResponse> {
    const formData = new FormData();
    formData.append('file', payload.file, payload.file.name);
    formData.append('schema_id', payload.schema_id);
    formData.append('schema_name', payload.schema_name);
    formData.append('type', payload.type);
    formData.append('created_by_uid', payload.created_by_uid);
    formData.append('created_by', payload.created_by);
    if (payload.sheet_name)   formData.append('sheet_name', payload.sheet_name);
    if (payload.header_row != null) formData.append('header_row', String(payload.header_row));
    if (payload.confirm != null) formData.append('confirm', String(payload.confirm));
    if (payload.scope)   formData.append('scope', JSON.stringify(payload.scope));

    return this.http.post<CreateCollectorDatasetResponse>(
      `${this.baseUrl}/collector-datasets`,
      formData
    );
  }

  uploadDatasetFile$(formData: FormData): Observable<{ file_url: string }> {
    return this.http
      .post<{ folderName: string; fileName: string[] }>(`${this.baseUrl}/gcs/upload`, formData)
      .pipe(
        map((res) => ({
          file_url: `${res.folderName}/${res.fileName?.[0] ?? ''}`
        }))
      );
  }

  buildCollectorUploadFormData(file: File): FormData {
    const formData = new FormData();
    formData.append('files', file, file.name);
    formData.append('bucketName', this.collectorBucketName);
    formData.append('folder', this.collectorFolderName);
    return formData;
  }

  emitValidationEvent$(payload: {
    collector_dataset_id: string;
    schema_id: string;
    file_url: string;
  }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/collector-datasets/validate`,
      payload
    );
  }

  createDataset$(payload: CreateDatasetPayload): Observable<string> {
    const collectionRef = collection(this.firestore, 'collector_datasets');

    return from(
      addDoc(collectionRef, {
        ...payload,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      })
    ).pipe(map((docRef) => docRef.id));
  }

  updateDataset$(
    datasetId: string,
    payload: Omit<CreateDatasetPayload, 'created_by' | 'created_by_uid'>
  ): Observable<void> {
    const datasetRef = doc(this.firestore, 'collector_datasets', datasetId);
    return from(
      updateDoc(datasetRef, {
        ...payload,
        updated_at: serverTimestamp()
      })
    );
  }

  watchDataset$(id: string): Observable<DatasetFile | null> {
    const ref = doc(this.firestore, 'collector_datasets', id);
    return docData(ref).pipe(
      map(data => data ? ({ id, ...data } as DatasetFile) : null),
      catchError(() => of(null))
    );
  }

  watchDatasetsBySchema$(schemaId: string, limitItems = 30): Observable<DatasetFile[]> {
    return new Observable<DatasetFile[]>(subscriber => {
      const q = query(
        collection(this.firestore, 'collector_datasets'),
        where('schema_id', '==', schemaId),
        orderBy('created_at', 'desc'),
        limit(limitItems)
      );

      let currentUnsub: (() => void) | null = null;

      currentUnsub = onSnapshot(
        q,
        snap => subscriber.next(snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<DatasetFile, 'id'>) }))),
        () => {
          currentUnsub?.();
          const fallbackQ = query(
            collection(this.firestore, 'collector_datasets'),
            where('schema_id', '==', schemaId),
            limit(limitItems)
          );
          currentUnsub = onSnapshot(
            fallbackQ,
            snap => subscriber.next(
              snap.docs
                .map(d => ({ id: d.id, ...(d.data() as Omit<DatasetFile, 'id'>) }))
                .sort((a, b) => ((b.created_at as any)?.seconds ?? 0) - ((a.created_at as any)?.seconds ?? 0))
            ),
            err => subscriber.error(err)
          );
        }
      );

      return () => currentUnsub?.();
    });
  }

  getDatasetById$(datasetId: string): Observable<DatasetFile | null> {
    const datasetRef = doc(this.firestore, 'collector_datasets', datasetId);
    return from(getDoc(datasetRef)).pipe(
      map((snap) => {
        if (!snap.exists()) return null;
        return {
          id: snap.id,
          ...(snap.data() as Omit<DatasetFile, 'id'>)
        };
      })
    );
  }

  getRecentDatasetsByCurrentUser$(limitItems = 10): Observable<DatasetFile[]> {
    return authState(this.auth).pipe(
      switchMap((user) => {
        if (!user) return of([]);

        const q = query(
          collection(this.firestore, 'collector_datasets'),
          where('created_by_uid', '==', user.uid),
          orderBy('created_at', 'desc'),
          limit(limitItems)
        );

        return from(getDocs(q)).pipe(
          map((snap) =>
            snap.docs.map((d) => ({
              id: d.id,
              ...(d.data() as Omit<DatasetFile, 'id'>)
            }))
          )
        );
      })
    );
  }

  getDatasetsBySchema$(schemaId: string, limitItems = 30): Observable<DatasetFile[]> {
    const q = query(
      collection(this.firestore, 'collector_datasets'),
      where('schema_id', '==', schemaId),
      orderBy('created_at', 'desc'),
      limit(limitItems)
    );

    return from(getDocs(q)).pipe(
      map((snap) =>
        snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<DatasetFile, 'id'>)
        }))
      ),
      catchError(() => {
        const fallbackQ = query(
          collection(this.firestore, 'collector_datasets'),
          where('schema_id', '==', schemaId),
          limit(limitItems)
        );

        return from(getDocs(fallbackQ)).pipe(
          map((snap) =>
            snap.docs
              .map((d) => ({
                id: d.id,
                ...(d.data() as Omit<DatasetFile, 'id'>)
              }))
              .sort((a, b) => {
                const aTime = (a.created_at as any)?.seconds ?? 0;
                const bTime = (b.created_at as any)?.seconds ?? 0;
                return bTime - aTime;
              })
          )
        );
      })
    );
  }

  getDatasetsByCurrentUser$(): Observable<DatasetFile[]> {
    return authState(this.auth).pipe(
      switchMap((user) => {
        if (!user) return of([]);

        const q = query(
          collectionGroup(this.firestore, 'permissions'),
          where('uid', '==', user.uid)
        );

        return from(getDocs(q)).pipe(
          switchMap((permSnap) => {
            const parentRefs = permSnap.docs
              .map((d) => d.ref.parent.parent)
              .filter((r): r is NonNullable<typeof r> => !!r);

            if (!parentRefs.length) return of([]);

            return forkJoin(parentRefs.map((ref) => from(getDoc(ref)))).pipe(
              map((docs) =>
                docs
                  .filter((d) => d.exists())
                  .map((d) => ({
                    id: d.id,
                    ...(d.data() as Omit<DatasetFile, 'id'>)
                  }))
              )
            );
          })
        );
      })
    );
  }

  getDatasetPermissions$(datasetId: string): Observable<DatasetPermission[]> {
    const permissionsRef = collection(
      this.firestore,
      `collector_datasets/${datasetId}/permissions`
    );
    return from(getDocs(permissionsRef)).pipe(
      map((snap) =>
        snap.docs.map((permissionDoc) => ({
          uid: permissionDoc.id,
          ...(permissionDoc.data() as Omit<DatasetPermission, 'uid'>)
        }))
      )
    );
  }

  upsertDatasetPermission$(
    datasetId: string,
    permission: DatasetPermission
  ): Observable<void> {
    const permissionRef = doc(
      this.firestore,
      `collector_datasets/${datasetId}/permissions/${permission.uid}`
    );

    return from(
      setDoc(
        permissionRef,
        {
          ...permission,
          created_at: serverTimestamp()
        },
        { merge: true }
      )
    );
  }

  removeDatasetPermission$(datasetId: string, uid: string): Observable<void> {
    const permissionRef = doc(
      this.firestore,
      `collector_datasets/${datasetId}/permissions/${uid}`
    );
    return from(deleteDoc(permissionRef));
  }
}
